import { Signal } from "signals";
import { GameConfigSchema } from "slotworks/config/game-config-schema";
import SlotDefinition from "slotworks/model/slot-definition";
import { Services } from "slotworks/services/services";
import ControlOptions from "slotworks/smart-ui/options/control-options";
import ValueList from "slotworks/utils/value-list";

class BetModel {
    private static SELECTED_LINES: string = "selectedLines";
    private static STAKE_PER_LINE: string = "creditPerLine";
    private static CREDIT_SIZE: string = "creditSize";

    public saveBetOptions: boolean = true;

    public freeBet: boolean = false;

    public creditsPerLine: ValueList<number> = new ValueList();
    public creditSizes: ValueList<number> = new ValueList();

    public onCreditSizeChanged: Signal = new Signal();
    public onCreditPerLineChanged: Signal = new Signal();
    public onSelectedLinesChanged: Signal = new Signal();

    private selectedLines: number[];
    private fixedBetCost: number = 0;

    private isLoaded: boolean;

    public processConfig(gameConfig: GameConfigSchema) {
        this.setDefaults(gameConfig.defaultCreditPerLine, gameConfig.defaultLines, gameConfig.defaultCreditSize);
    }

    public setDefaults(creditPerLine: number, lines: number, creditSize: number) {
        this.creditSizes.defaultValue = creditSize;
        this.creditsPerLine.defaultValue = creditPerLine;

        this.setCreditSize(creditSize);
        this.setCreditPerLine(creditPerLine);
        this.setSelectedLines(lines);
    }

    // Sets a fixed cost instead of multiplying bet per line by number of lines
    // If > 0, total bet is calculated as creditSize * creditsPerLine * fixedBetCost
    // If 0, total bet is calculated as creditSize * creditsPerLine * selectedLines
    public setFixedBetCost(value: number) {
        this.fixedBetCost = value;
    }

    public getFixedBetCost() {
        return this.fixedBetCost;
    }

    public getCreditPerLine(): number {
        return this.creditsPerLine.currentValue;
    }

    public getCreditSize(): number {
        return this.creditSizes.currentValue;
    }

    public getSelectedLines(): number {
        return this.selectedLines.length || 1;
    }

    public getTotalCoinBet(): number {
        if (this.fixedBetCost > 0) {
            return this.getCreditPerLine() * this.fixedBetCost;
        }
        return this.getCreditPerLine() * this.getSelectedLines();
    }

    public getTotalStake(): number {
        return this.getTotalCoinBet() * this.getCreditSize();
    }

    public injectBetOptions(options: ControlOptions): ControlOptions {
        options.creditsPerLine = this.creditsPerLine.clone();

        options.creditSizes = this.creditSizes.clone();

        options.selectedLines = this.getSelectedLines();

        options.fixedBetCost = this.getFixedBetCost();

        return options;
    }

    public load() {
        if (this.saveBetOptions) {
            const options = new ControlOptions();

            const creditSize = Services.storage.getNumber(BetModel.CREDIT_SIZE);
            const stakePerLine = Services.storage.getNumber(BetModel.STAKE_PER_LINE);
            const selectedLines = Services.storage.getNumber(BetModel.SELECTED_LINES);

            if (creditSize !== null && stakePerLine !== null && selectedLines !== null) {
                options.creditSizes.currentValue = creditSize;
                options.creditsPerLine.currentValue = stakePerLine;
                options.selectedLines = selectedLines;

                this.updateFromOptions(options);
            }

        }
        this.isLoaded = true;
    }

    public updateFromOptions(options: ControlOptions): void {
        if (options.creditSizes.hasData()) {
            this.setCreditSize(options.creditSizes.currentValue);
        }
        if (options.creditsPerLine.hasData()) {
            this.setCreditPerLine(options.creditsPerLine.currentValue);
        }
        if (options.selectedLines !== undefined) {
            this.setSelectedLines(options.selectedLines);
        }
    }

    public getSelectedLineIds(): number[] {
        return this.selectedLines;
    }

    private setSelectedLines(lines: number) {
        const newLines = [];

        for (let i = 1; i <= lines; i++) {
            newLines.push(i);
        }

        this.selectedLines = newLines;

        SlotDefinition.lines.setCurrentIndex(lines);

        this.onSelectedLinesChanged.dispatch(lines);
        this.save();
    }

    private setCreditSize(value: number) {
        if (this.creditSizes.currentValue !== value) {
            this.creditSizes.currentValue = value;
            Services.currency.creditRatio = value;
            this.onCreditSizeChanged.dispatch(value);
            this.save();
        }
    }

    private setCreditPerLine(value: number) {
        if (this.creditsPerLine.currentValue !== value) {
            this.creditsPerLine.currentValue = value;
            this.onCreditPerLineChanged.dispatch(value);
            this.save();
        }
    }

    private save() {
        if (this.saveBetOptions && this.isLoaded) {
            Services.storage.set(BetModel.STAKE_PER_LINE, this.getCreditPerLine());
            Services.storage.set(BetModel.SELECTED_LINES, this.getSelectedLines());
            Services.storage.set(BetModel.CREDIT_SIZE, this.getCreditSize());
        }
    }
}

export default new BetModel();
